import React, { useState } from "react";
import {
    Box,
    Button,
    Chip,
    Collapse,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
    alpha,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import { ITEM_TYPES, itemTypeMeta } from "./plannerConfig";
import { formatHours, rollupGroupQuote } from "./plannerQuoteRates";
import { formatMoney } from "./quotePricingMath";

function BranchNode({
    node,
    depth,
    estimateLines,
    currency,
    onAddBranches,
    onRemove,
    onPatch,
    onPatchItem,
    onAddItem,
    onRemoveItem,
    onAddEstimateLine,
    color,
}) {
    const [open, setOpen] = useState(depth < 1);
    const items = node.items || [];
    const quote = rollupGroupQuote(node);
    const groupLines = (estimateLines || []).filter((L) => L.group_id === node.id);
    const lineTotal = groupLines.reduce((s, L) => s + (Number(L.qty) || 0) * (Number(L.bill_rate) || 0), 0);
    const money = (n) => formatMoney(n, currency || "USD");

    return (
        <Box sx={{ ml: depth * 2.5, mb: 1 }}>
            <Box
                sx={(theme) => ({
                    border: 1,
                    borderColor: "divider",
                    borderLeft: `3px solid ${color || theme.palette.primary.main}`,
                    bgcolor: alpha(color || theme.palette.primary.main, 0.04),
                })}
            >
                <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ px: 1.5, py: 1 }}>
                    {depth > 0 ? <SubdirectoryArrowRightIcon sx={{ fontSize: 18, color: "text.disabled" }} /> : null}
                    <IconButton size="small" onClick={() => setOpen((v) => !v)}>
                        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                    <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={700}>{node.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {items.length} need{items.length === 1 ? "" : "s"}
                            {quote.own.laborHours > 0 ? ` · ${formatHours(quote.own.laborHours)} @ $${quote.profile.hourlyRate}/hr` : ` · ${quote.profile.label} @ $${quote.profile.hourlyRate}/hr`}
                            {quote.own.total > 0 ? ` · Budget ${money(quote.own.total)}` : ""}
                        </Typography>
                    </Box>
                    {groupLines.length > 0 ? (
                        <Chip size="small" label={new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD", maximumFractionDigits: 0 }).format(lineTotal)} />
                    ) : null}
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => onAddBranches(node)} sx={{ whiteSpace: "nowrap" }}>
                        Add branch
                    </Button>
                    <IconButton size="small" color="error" onClick={() => onRemove(node.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Stack>

                <Collapse in={open}>
                    <Box sx={{ px: 2, pb: 2, borderTop: 1, borderColor: "divider" }}>
                        <Stack spacing={1.5} sx={{ pt: 1.5 }}>
                            <TextField size="small" label="Label" value={node.label} onChange={(e) => onPatch(node.id, { label: e.target.value })} fullWidth />
                            <TextField size="small" label="Scope notes" multiline minRows={2} value={node.scope_notes || ""} onChange={(e) => onPatch(node.id, { scope_notes: e.target.value })} fullWidth />
                            <Box sx={{ p: 1.5, border: 1, borderColor: "divider", bgcolor: "action.hover" }}>
                                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                    QUOTE TOOLS · {quote.profile.label} @ ${quote.profile.hourlyRate}/hr
                                </Typography>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                                    <TextField
                                        size="small"
                                        label="Labor hours"
                                        type="number"
                                        value={node.labor_hours || ""}
                                        onChange={(e) => onPatch(node.id, { labor_hours: e.target.value === "" ? 0 : Math.max(0, Number(e.target.value) || 0) })}
                                        inputProps={{ min: 0, step: 0.5 }}
                                        sx={{ minWidth: 130 }}
                                    />
                                    <TextField
                                        size="small"
                                        label="Labor cost"
                                        value={quote.own.laborHours > 0 ? money(quote.own.cost) : "—"}
                                        InputProps={{ readOnly: true }}
                                        helperText={quote.own.laborHours > 0 ? "From labor × rate" : "Enter hours"}
                                        sx={{ minWidth: 140 }}
                                    />
                                    <TextField
                                        size="small"
                                        label="Purchases / materials"
                                        type="number"
                                        value={node.material_cost || ""}
                                        onChange={(e) => onPatch(node.id, { material_cost: e.target.value === "" ? 0 : Math.max(0, Number(e.target.value) || 0) })}
                                        inputProps={{ min: 0, step: 50 }}
                                        sx={{ minWidth: 160 }}
                                    />
                                    <TextField
                                        size="small"
                                        label="Budget total"
                                        value={quote.own.total > 0 ? money(quote.own.total) : "—"}
                                        InputProps={{ readOnly: true }}
                                        helperText={quote.children.total > 0 ? `With children ${money(quote.total)}` : "Own cost + purchases"}
                                        sx={{ minWidth: 150 }}
                                    />
                                </Stack>
                            </Box>
                            <FormControlLabel control={<Switch size="small" checked={node.enabled !== false} onChange={(e) => onPatch(node.id, { enabled: e.target.checked })} />} label="Include in quote" />
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="caption" fontWeight={700} color="text.secondary">CUSTOMER NEEDS</Typography>
                                <Button size="small" onClick={() => onAddItem(node.id)}>+ Note</Button>
                            </Stack>
                            {items.map((it) => (
                                <Box key={it.id} sx={{ p: 1, border: 1, borderColor: "divider" }}>
                                    <Stack direction="row" spacing={0.5}>
                                        <TextField size="small" fullWidth placeholder="What does the customer need?" value={it.title} onChange={(e) => onPatchItem(node.id, it.id, { title: e.target.value })} />
                                        <IconButton size="small" color="error" onClick={() => onRemoveItem(node.id, it.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                                    </Stack>
                                    <FormControl size="small" sx={{ mt: 1, minWidth: 120 }}>
                                        <InputLabel>Type</InputLabel>
                                        <Select label="Type" value={it.item_type} onChange={(e) => onPatchItem(node.id, it.id, { item_type: e.target.value })}>
                                            {ITEM_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <Chip size="small" label={itemTypeMeta(it.item_type).label} sx={{ ml: 1, mt: 1 }} variant="outlined" />
                                </Box>
                            ))}
                            <Button size="small" variant="text" onClick={() => onAddEstimateLine(node)}>Link estimate line →</Button>
                        </Stack>
                    </Box>
                </Collapse>
            </Box>
            {node.children?.map((child) => (
                <BranchNode key={child.id} node={child} depth={depth + 1} estimateLines={estimateLines} currency={currency} onAddBranches={onAddBranches} onRemove={onRemove} onPatch={onPatch} onPatchItem={onPatchItem} onAddItem={onAddItem} onRemoveItem={onRemoveItem} onAddEstimateLine={onAddEstimateLine} color={color} />
            ))}
        </Box>
    );
}

export default function PlanBranchTree(props) {
    const { tree } = props;
    if (!tree?.length) return null;
    return (
        <Box>
            {tree.map((node) => (
                <BranchNode key={node.id} node={node} depth={0} color={props.definitionColors?.[node.group_key]} {...props} />
            ))}
        </Box>
    );
}
